import { useAppDispatch, useAppSelector } from "@/shared/lib/redux"
import { Loader } from "@/shared/ui/loader"
import { NoTableData } from "@/shared/ui/no-table-data"
import { Table } from "@/shared/ui/table"
import { ColumnDef } from "@tanstack/react-table"
import { IMarketingCampaing } from "@/shared/api/marketing-campaigns"
import { toLocaleDateTimeString } from "@/shared/lib/date-formatters"
import CheckSvg from '@/shared/assets/images/Check.svg'
import XSvg from '@/shared/assets/images/X.svg'
import { ActionButton } from "@/shared/ui/action-button"
import PowerSvg from '@/shared/assets/images/Power.svg'
import { useUpdateCampaignModal } from "@/features/update-marketing-campaigns"
import { fetchCampaigns, selectCurrentPage, selectMarketingCampaigns, selectMarketingCampaignsLoading, selectTotalPages, setPage } from "@/entities/marketing-campaign"
import { Pagination } from "@/shared/ui/pagination"
import { useNavigate } from "react-router-dom"
import ArrowLeftSvg from '@/shared/assets/images/ArrowBendDownRight.svg'
import ChatSvg from '@/shared/assets/images/MessengerLogo.svg'
import ResultsSvg from '@/shared/assets/images/Broadcast.svg'

export const MarketingCampaignsTable = () => {
    const campaigns = useAppSelector(selectMarketingCampaigns)
    const isLoading = useAppSelector(selectMarketingCampaignsLoading)
    const { openToggleCampaignModal, openPostResultsModal } = useUpdateCampaignModal()
    const dispatch = useAppDispatch()
    const navigate = useNavigate()
    const page = useAppSelector(selectCurrentPage)
    const totalPages = useAppSelector(selectTotalPages)

    const columns: ColumnDef<IMarketingCampaing, any>[] = [
        {
            accessorFn: (campaign) => (
                <div className="w-full">
                    {campaign.name}
                </div>
            ),
            id: 'name',
            enableSorting: false,
            cell: info => info.getValue(),
            header: () => <div className="!text-start text-nowrap">Название кампании</div>,
        },
        {
            accessorFn: (campaign) => (
                <div>
                    {toLocaleDateTimeString (campaign.start_date)}
                </div>
            ),
            id: 'startDate',
            enableSorting: false,
            cell: info => info.getValue(),
            header: () => <div className="!text-start text-nowrap">Дата начала</div>,
        },
        {
            accessorFn: (campaign) => (
                <div>
                    {toLocaleDateTimeString(campaign.end_date)}
                </div>
            ),
            id: 'endDate',
            enableSorting: false,
            cell: info => info.getValue(),
            header: () => <div className="!text-start text-nowrap">Дата подведения итогов</div>,
        },
        {
            accessorFn: (campaign) => (
                <div>
                    {campaign.limit === 0 ? 'Без ограничений' : campaign.limit}
                </div>
            ),
            id: 'limit',
            enableSorting: false,
            cell: info => info.getValue(),
            header: () => <div className="!text-start text-nowrap">Количетсво призовых мест</div>,
        },
        {
            accessorFn: (campaign) => (
                <div className="w-full flex items-end">
                    {campaign.is_finished
                        ? <div>
                            Завершена
                        </div>
                        : <div>
                            Ожидает завершения
                        </div>
                    }
                </div>
            ),
            id: 'status',
            enableSorting: false,
            meta: {
                cellProps: {
                    className: 'w-full [&_div]:!justify-start'
                }
            },
            cell: info => info.getValue(),
            header: () => <div>Статус</div>,
        },
        {
            accessorFn: (campaign) => (
                <div className="w-full flex items-end">
                    {campaign.is_active
                        ? <div className="rounded-full ml-auto h-6 w-6 bg-color-primary text-white flex items-center justify-center">
                            <CheckSvg width={16} height={16} />
                        </div>
                        : <div className="rounded-full ml-auto h-6 w-6 bg-color-danger text-white flex items-center justify-center">
                            <XSvg width={16} height={16} />
                        </div>
                    }
                </div>
            ),
            id: 'is_active',
            enableSorting: false,
            meta: {
                cellProps: {
                    className: 'w-full [&_div]:!justify-end'
                }
            },
            cell: info => info.getValue(),
            header: () => <div>Активна</div>,
        },
        {
            accessorFn: (campaign) => (
                <div className="w-full flex justify-end gap-2">
                    {campaign.is_finished &&
                        <ActionButton
                            onClick={() => openPostResultsModal(campaign)}
                            icon={ResultsSvg}
                        />
                    }
                    {!campaign.is_finished &&
                        <ActionButton
                            onClick={() => openToggleCampaignModal(campaign, () => dispatch(fetchCampaigns()))}
                            icon={PowerSvg}
                        />
                    }
                    {!campaign.is_finished && campaign.is_draw &&
                        <ActionButton
                            onClick={() => navigate(`/marketing-campaigns/campaign/${campaign.id}/announce`)}
                            icon={ChatSvg}
                        />
                    }
                    <ActionButton
                        onClick={() => navigate(`/marketing-campaigns/campaign/${campaign.id}`)}
                        icon={ArrowLeftSvg}
                    />
                </div>
            ),
            id: 'actions',
            enableSorting: false,
            cell: info => info.getValue(),
            header: () => <div />,
        },
    ]

    return (
        <>
            {Boolean(campaigns.length) &&
                <div className="table_box">
                    <Table columns={columns} data={campaigns} />
                    <Pagination 
                        page={page} 
                        totalPages={totalPages} 
                        setPage={(page) => dispatch(setPage(page))}
                    />
                </div>
            }
            {(!Boolean(campaigns.length) && isLoading) &&
                <div className="info_panel">
                    <Loader />
                </div>
            }
            {(!Boolean(campaigns.length) && !isLoading) &&
                <NoTableData label="Нет данных о кампаниях" />
            }
        </>
    )
}